import { useEffect, useState } from "react";
import Layout from "../components/Layout";
import api, { formatCurrency, formatQty, unitLabel, API, whatsappInvoiceLink, downloadAuthedFile } from "../lib/api";
import { Plus, FileText, X, Receipt, MessageCircle, ScanLine } from "lucide-react";
import QrScannerModal from "../components/QrScannerModal";
import { toast, Toaster } from "sonner";
import ResponsiveTable from "../components/ResponsiveTable";

export default function MySales() {
  const [sales, setSales] = useState([]);
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [me, setMe] = useState(null);
  const [saleModal, setSaleModal] = useState(false);

  const load = async () => {
    const [s, p, c, my] = await Promise.all([
      api.get("/accounting/sales"),
      api.get("/products"),
      api.get("/crm/customers"),
      api.get("/sales-users/me").catch(() => ({ data: null })),
    ]);
    setSales(s.data);
    setProducts(p.data.filter((x) => x.status !== "Sold" && x.quantity > 0));
    setCustomers(c.data);
    setMe(my.data);
  };

  useEffect(() => { load(); }, []);

  const myMonthSales = sales
    .filter((s) => {
      const dt = new Date(s.created_at);
      const now = new Date();
      return dt.getUTCFullYear() === now.getUTCFullYear() && dt.getUTCMonth() === now.getUTCMonth();
    })
    .reduce((acc, s) => acc + (s.total || 0), 0);

  const downloadInvoice = async (sale) => {
    try {
      await downloadAuthedFile(`/accounting/invoice/${sale.id}`, `${sale.invoice_number}.pdf`);
    } catch (err) {
      toast.error(err.message || "Could not download invoice");
    }
  };

  return (
    <Layout
      title="My Sales"
      subtitle="Record & track your invoices"
      action={
        <button
          data-testid="seller-record-sale-btn"
          onClick={() => setSaleModal(true)}
          className="btn-gold px-5 py-3 inline-flex items-center gap-2"
        >
          <Plus className="w-3.5 h-3.5" /> Record Sale
        </button>
      }
    >
      <Toaster theme="dark" position="top-right" />

      {/* Top stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-10">
        <div className="glass-card p-7">
          <div className="overline flex items-center gap-2"><Receipt className="w-3 h-3" /> This Month</div>
          <div className="serif text-3xl mt-3 text-[#D4AF37]" data-testid="my-month-sales">{formatCurrency(myMonthSales)}</div>
          <div className="mono text-xs text-[#8A8A8A] mt-2">
            {sales.filter((s) => {
              const dt = new Date(s.created_at); const now = new Date();
              return dt.getUTCFullYear() === now.getUTCFullYear() && dt.getUTCMonth() === now.getUTCMonth();
            }).length} sales recorded
          </div>
        </div>
        <div className="glass-card p-7">
          <div className="overline">All-time Revenue</div>
          <div className="serif text-3xl mt-3" data-testid="my-total-sales">{formatCurrency(sales.reduce((a, b) => a + (b.total || 0), 0))}</div>
          <div className="mono text-xs text-[#8A8A8A] mt-2">{sales.length} sales</div>
        </div>
        <div className="glass-card p-7">
          <div className="overline">Default Commission</div>
          <div className="serif text-3xl mt-3 text-[#7BA585]" data-testid="my-rate">
            {me ? `${(me.commission_rate || 0).toFixed(1)}%` : "—"}
          </div>
          <div className="mono text-xs text-[#8A8A8A] mt-2">
            {me?.monthly_target ? `Target ${formatCurrency(me.monthly_target)}` : "No monthly target set"}
          </div>
        </div>
      </div>

      {/* Sales table */}
      <MySalesTable sales={sales} onDownloadInvoice={downloadInvoice} />

      {/* Empty-state hints */}
      {customers.length === 0 && (
        <div className="mt-6 glass-card p-5 text-xs text-[#8A8A8A]" data-testid="no-customers-warning">
          No customers in your CRM yet. You can add one on the fly by typing a new name in the Record Sale form.
        </div>
      )}
      {products.length === 0 && (
        <div className="mt-3 glass-card p-5 text-xs text-[#8A8A8A]">
          No existing stock visible to you — type a new piece name in Record Sale to add and bill it in one step.
        </div>
      )}

      {saleModal && (
        <SellerSaleModal
          products={products}
          customers={customers}
          me={me}
          onClose={() => setSaleModal(false)}
          onSaved={() => { setSaleModal(false); load(); }}
        />
      )}
    </Layout>
  );
}

// ---------- SELLER POS-STYLE MULTI-PRODUCT SALE MODAL ----------
const blankSellerLine = () => ({
  key: Math.random().toString(36).slice(2),
  product_id: "",
  productInput: "",
  variant_id: "",
  quantity: 1,
  unit_price: 0,
});

function SellerSaleModal({ products, customers, me, onClose, onSaved }) {
  const [customer_id, setCid] = useState("");
  const [customerInput, setCustomerInput] = useState("");
  const [customCustomerPhone, setCustomCustomerPhone] = useState("");
  const [customCustomerAddress, setCustomCustomerAddress] = useState("");
  const [items, setItems] = useState([blankSellerLine()]);
  const [invoice_discount, setInvoiceDiscount] = useState(0);
  const [tax_pct, setTaxPct] = useState(0);
  const [payment_method, setPay] = useState("Wire");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  // POS scanner for sales role
  const [showScanner, setShowScanner] = useState(false);

  const productDisplay = (p) => `${p.sku} · ${p.name}`;
  const matchedCustomer = customers.find((c) => c.name === customerInput);
  useEffect(() => {
    setCid(matchedCustomer ? matchedCustomer.id : "");
    // eslint-disable-next-line
  }, [customerInput]);
  const isCustomCustomer = !customer_id && customerInput.trim().length > 0;

  const updateLine = (idx, patch) => {
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  };
  const removeLine = (idx) => setItems((prev) => prev.filter((_, i) => i !== idx));
  const addLine = () => setItems((prev) => [...prev, blankSellerLine()]);

  /** POS scanner callback — same pattern as Accounting.js */
  const handleScannedProduct = (_shortId, prod) => {
    const lineDefaults = {
      productInput: `${prod.sku} · ${prod.name}`,
      product_id: prod.id,
      variant_id: "",
      quantity: 1,
      unit_price: Number(prod.selling_price || 0),
      discount: 0,
      notes: "",
    };
    setItems((prev) => {
      const blankIdx = prev.findIndex((it) => !it.productInput && !it.product_id);
      if (blankIdx >= 0) {
        const next = [...prev];
        next[blankIdx] = { ...next[blankIdx], ...lineDefaults };
        return next;
      }
      return [...prev, { ...blankSellerLine(), ...lineDefaults }];
    });
    toast.success(`+ ${prod.sku}`);
  };

  const handleProductInput = (idx, text) => {
    const matched = products.find((p) => productDisplay(p) === text || p.name === text);
    const patch = { productInput: text, variant_id: "" };
    if (matched) {
      patch.product_id = matched.id;
      patch.unit_price = matched.selling_price || 0;
    } else {
      patch.product_id = "";
    }
    updateLine(idx, patch);
  };
  const handleVariantChange = (idx, variantId) => {
    const line = items[idx];
    const prod = products.find((p) => p.id === line.product_id);
    const v = prod?.variants?.find((x) => x.id === variantId);
    const patch = { variant_id: variantId };
    if (v && Number(v.selling_price) > 0) patch.unit_price = v.selling_price;
    else if (prod) patch.unit_price = prod.selling_price || 0;
    updateLine(idx, patch);
  };

  const lineMeta = items.map((it) => {
    const prod = products.find((p) => p.id === it.product_id) || null;
    const variants = prod?.variants || [];
    const hasVariants = variants.length > 0;
    const selectedVariant = hasVariants ? variants.find((v) => v.id === it.variant_id) : null;
    // Read STOCK (admin inventory count), falling back to legacy `quantity` for un-migrated rows.
    const stockAvail = selectedVariant
      ? (selectedVariant.stock != null ? selectedVariant.stock : selectedVariant.quantity)
      : (prod ? (prod.stock != null ? prod.stock : (prod.quantity || 0)) : 0);
    const stockUnit = selectedVariant
      ? (selectedVariant.stock_unit || prod?.unit || "pcs")
      : (prod?.stock_unit || prod?.unit || "pcs");
    const stockCustomUnit = selectedVariant
      ? (selectedVariant.stock_custom_unit || prod?.custom_unit || "")
      : (prod?.stock_custom_unit || prod?.custom_unit || "");
    const qty = parseFloat(it.quantity) || 0;
    const price = parseFloat(it.unit_price) || 0;
    const subtotal = qty * price;
    const variantNotPicked = hasVariants && !it.variant_id;
    const isCustom = !it.product_id && it.productInput.trim().length > 0;
    const stockBlocked = prod && !isCustom && qty > stockAvail + 1e-9;
    return { prod, hasVariants, variants, selectedVariant, stockAvail, stockUnit, stockCustomUnit, qty, price, subtotal, variantNotPicked, isCustom, stockBlocked };
  });

  const subtotal_amount = lineMeta.reduce((a, m) => a + m.subtotal, 0);
  const afterDiscount = Math.max(0, subtotal_amount - (parseFloat(invoice_discount) || 0));
  const tax_amount = afterDiscount * ((parseFloat(tax_pct) || 0) / 100);
  const grand_total = afterDiscount + tax_amount;
  const rate = me?.commission_rate || 0;
  const commissionPreview = grand_total * (rate / 100);

  const anyBlocked = lineMeta.some((m) => m.stockBlocked || m.variantNotPicked);
  const allLinesFilled = items.every((it) => it.productInput.trim().length > 0 && parseFloat(it.quantity) > 0 && parseFloat(it.unit_price) >= 0);

  const submit = async (e) => {
    e.preventDefault();
    if (!customerInput.trim()) return toast.error("Customer required");
    if (isCustomCustomer && !customCustomerPhone.trim()) return toast.error("Mobile number required for new customer");
    if (!allLinesFilled) return toast.error("Fill all line items");
    if (anyBlocked) return toast.error("Fix line errors (variant or stock)");
    setSubmitting(true);
    try {
      const payload = {
        customer_id,
        custom_customer_name: isCustomCustomer ? customerInput.trim() : "",
        custom_customer_phone: isCustomCustomer ? customCustomerPhone.trim() : "",
        custom_customer_address: isCustomCustomer ? customCustomerAddress.trim() : "",
        items: items.map((it, i) => {
          const meta = lineMeta[i];
          return {
            product_id: it.product_id || "",
            variant_id: it.variant_id || "",
            custom_product_name: meta.isCustom ? it.productInput.trim() : "",
            quantity: parseFloat(it.quantity),
            unit_price: parseFloat(it.unit_price),
          };
        }),
        invoice_discount: parseFloat(invoice_discount) || 0,
        tax_pct: parseFloat(tax_pct) || 0,
        payment_method,
        notes,
      };
      await api.post("/accounting/sales/multi", payload);
      toast.success(`Invoice saved · ${items.length} item${items.length === 1 ? "" : "s"} · Commission ${formatCurrency(commissionPreview)}`);
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-start sm:items-center justify-center p-4 overflow-y-auto">
      <div className="relative bg-[#0F0F0F] border border-white/10 w-full max-w-5xl max-h-[92vh] overflow-y-auto fade-up">
        <div className="sticky top-0 z-10 bg-[#0F0F0F] border-b border-white/5 px-7 py-5 flex justify-between items-center">
          <div>
            <div className="overline">New Invoice · POS</div>
            <h3 className="serif text-2xl mt-1">Record My Sale</h3>
          </div>
          <button onClick={onClose} data-testid="seller-sale-close"><X className="w-5 h-5 text-[#8A8A8A]" /></button>
        </div>
        <form onSubmit={submit} className="px-7 py-6 space-y-5" data-testid="seller-sale-form">
          <Field label="Customer (type to search or add new)">
            <input
              required
              list="gv-seller-customers"
              value={customerInput}
              onChange={(e) => setCustomerInput(e.target.value)}
              placeholder="Search your customers — or type a new name"
              className="luxe-input w-full px-4 py-2.5 text-sm"
              data-testid="seller-sale-customer"
            />
            <datalist id="gv-seller-customers">
              {customers.map((c) => (
                <option key={c.id} value={c.name} label={c.company || c.email || ""} />
              ))}
            </datalist>
            {isCustomCustomer && (
              <div className="mt-1.5 text-[0.65rem] mono text-[#7BA585]" data-testid="seller-custom-customer-hint">
                + New customer "{customerInput.trim()}" will be added to your CRM
              </div>
            )}
          </Field>
          {isCustomCustomer && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 border border-[#7BA585]/15 p-4 bg-[#7BA585]/[0.025]" data-testid="seller-custom-customer-fields">
              <Field label="Mobile Number (required)">
                <input required type="tel" placeholder="+62 ..." value={customCustomerPhone} onChange={(e) => setCustomCustomerPhone(e.target.value)} className="luxe-input w-full px-4 py-2.5 text-sm mono" data-testid="seller-custom-customer-phone" />
              </Field>
              <Field label="Address (optional)">
                <input value={customCustomerAddress} onChange={(e) => setCustomCustomerAddress(e.target.value)} placeholder="Jakarta / Bangkok / ..." className="luxe-input w-full px-4 py-2.5 text-sm" data-testid="seller-custom-customer-address" />
              </Field>
            </div>
          )}

          {/* Line items table */}
          <div className="border border-[#D4AF37]/15 bg-[#D4AF37]/[0.02]">
            <div className="px-4 py-3 border-b border-white/5 flex items-center justify-between flex-wrap gap-2">
              <div className="overline text-[#D4AF37]">Line Items ({items.length})</div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setShowScanner(true)}
                  className="btn-gold px-3 py-1.5 text-[0.65rem] uppercase tracking-wider inline-flex items-center gap-1.5"
                  data-testid="seller-open-scanner"
                >
                  <ScanLine className="w-3.5 h-3.5" strokeWidth={1.5} />
                  Scan QR
                </button>
                <button type="button" onClick={addLine} className="btn-outline-gold px-3 py-1.5 text-[0.65rem] uppercase tracking-wider" data-testid="seller-add-line-item">
                  + Add product
                </button>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="seller-line-items-table">
                <thead>
                  <tr className="text-left overline text-[0.55rem] text-[#7a7a7a] border-b border-white/5">
                    <th className="px-3 py-2 font-normal w-[28%]">Product</th>
                    <th className="px-3 py-2 font-normal w-[18%]">Variant</th>
                    <th className="px-3 py-2 font-normal w-[10%]">Qty</th>
                    <th className="px-3 py-2 font-normal w-[8%]">Unit</th>
                    <th className="px-3 py-2 font-normal w-[14%]">Unit Price</th>
                    <th className="px-3 py-2 font-normal text-right w-[14%]">Subtotal</th>
                    <th className="px-3 py-2 w-[8%]"></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it, idx) => {
                    const meta = lineMeta[idx];
                    return (
                      <tr key={it.key} className="border-b border-white/5 align-top" data-testid={`seller-line-row-${idx}`}>
                        <td className="px-3 py-2">
                          <input
                            list={`gv-seller-line-products-${idx}`}
                            value={it.productInput}
                            onChange={(e) => handleProductInput(idx, e.target.value)}
                            placeholder="SKU or name"
                            className="luxe-input w-full px-2 py-2 text-xs"
                            data-testid={`seller-line-product-${idx}`}
                          />
                          <datalist id={`gv-seller-line-products-${idx}`}>
                            {products.map((p) => {
                              const sv = p.stock != null ? p.stock : (p.quantity || 0);
                              const su = p.stock_unit || p.unit || "pcs";
                              const scu = p.stock_custom_unit || p.custom_unit || "";
                              return (
                                <option key={p.id} value={productDisplay(p)} label={`${formatQty(sv, su, scu)} avail.`} />
                              );
                            })}
                          </datalist>
                          {meta.isCustom && (
                            <div className="mt-1 text-[0.6rem] mono text-[#7BA585]" data-testid={`seller-line-custom-hint-${idx}`}>+ auto-create</div>
                          )}
                          {meta.prod && (
                            <div className="mt-1 text-[0.6rem] mono text-[#666]">
                              {formatQty(meta.stockAvail, meta.stockUnit, meta.stockCustomUnit)} avail
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-2">
                          {meta.hasVariants ? (
                            <select
                              required
                              value={it.variant_id}
                              onChange={(e) => handleVariantChange(idx, e.target.value)}
                              className="luxe-input w-full px-2 py-2 text-xs"
                              data-testid={`seller-line-variant-${idx}`}
                            >
                              <option value="">— pick —</option>
                              {meta.variants.map((v) => {
                                const label = [v.color, v.size_mm ? `${v.size_mm}mm` : v.size_label].filter(Boolean).join(" · ");
                                const priceTxt = Number(v.selling_price) > 0
                                  ? ` · Rp ${Number(v.selling_price).toLocaleString("id-ID", { maximumFractionDigits: 0 })}`
                                  : "";
                                const vStock = v.stock != null ? v.stock : v.quantity;
                                const vUnit = v.stock_unit || meta.prod?.unit || "pcs";
                                const vCustom = v.stock_custom_unit || meta.prod?.custom_unit || "";
                                return (
                                  <option key={v.id} value={v.id} disabled={vStock <= 0}>
                                    {label}{priceTxt} — {formatQty(vStock, vUnit, vCustom)} avail.
                                  </option>
                                );
                              })}
                            </select>
                          ) : (
                            <span className="text-[#666] text-[0.65rem]">—</span>
                          )}
                        </td>
                        <td className="px-3 py-2">
                          <input
                            type="number" step="0.01" min="0.01"
                            value={it.quantity}
                            onChange={(e) => updateLine(idx, { quantity: e.target.value })}
                            className="luxe-input w-full px-2 py-2 text-xs mono"
                            data-testid={`seller-line-qty-${idx}`}
                          />
                        </td>
                        <td className="px-3 py-2 text-[0.65rem] text-[#8A8A8A] mono pt-4">
                          {meta.prod ? unitLabel(meta.prod.unit, meta.prod.custom_unit) : "pcs"}
                        </td>
                        <td className="px-3 py-2">
                          <input
                            type="number" step="1" min="0"
                            value={it.unit_price}
                            onChange={(e) => updateLine(idx, { unit_price: e.target.value })}
                            className="luxe-input w-full px-2 py-2 text-xs mono"
                            data-testid={`seller-line-price-${idx}`}
                          />
                        </td>
                        <td className="px-3 py-2 text-right mono text-[#D4AF37]" data-testid={`seller-line-subtotal-${idx}`}>
                          {formatCurrency(meta.subtotal)}
                        </td>
                        <td className="px-3 py-2 text-center">
                          {items.length > 1 && (
                            <button type="button" onClick={() => removeLine(idx)} className="text-[#C99356] hover:text-red-400 text-[0.6rem] uppercase tracking-wider" data-testid={`seller-line-remove-${idx}`}>
                              Remove
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {anyBlocked && (
              <div className="px-4 py-2 text-[0.65rem] text-red-400 border-t border-red-400/20 bg-red-400/[0.05]" data-testid="seller-line-error">
                Fix line errors: pick a variant for variant-products and ensure quantity ≤ stock available.
              </div>
            )}
          </div>

          {/* Summary */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="space-y-3">
              <Field label="Payment Method">
                <select value={payment_method} onChange={(e) => setPay(e.target.value)} className="luxe-input w-full px-4 py-2.5 text-sm">
                  {["Cash", "Wire", "Card", "Crypto"].map((p) => <option key={p}>{p}</option>)}
                </select>
              </Field>
              <Field label="Notes">
                <input value={notes} onChange={(e) => setNotes(e.target.value)} className="luxe-input w-full px-4 py-2.5 text-sm" />
              </Field>
            </div>
            <div className="bg-[#0A0A0A] border border-[#D4AF37]/20 p-5 space-y-3" data-testid="seller-invoice-summary">
              <div className="flex justify-between text-xs">
                <span className="text-[#8A8A8A]">Total Items</span>
                <span className="mono">{items.length}</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-[#8A8A8A]">Subtotal</span>
                <span className="mono" data-testid="seller-summary-subtotal">{formatCurrency(subtotal_amount)}</span>
              </div>
              <div className="flex justify-between items-center text-xs">
                <span className="text-[#8A8A8A]">Discount (IDR)</span>
                <input type="number" step="1" min="0" value={invoice_discount} onChange={(e) => setInvoiceDiscount(e.target.value)} className="luxe-input w-32 px-2 py-1.5 text-xs mono text-right" data-testid="seller-summary-discount" />
              </div>
              <div className="flex justify-between items-center text-xs">
                <span className="text-[#8A8A8A]">Tax (%)</span>
                <input type="number" step="0.1" min="0" max="100" value={tax_pct} onChange={(e) => setTaxPct(e.target.value)} className="luxe-input w-32 px-2 py-1.5 text-xs mono text-right" placeholder="0" data-testid="seller-summary-tax-pct" />
              </div>
              {parseFloat(tax_pct) > 0 && (
                <div className="flex justify-between text-xs">
                  <span className="text-[#8A8A8A]">Tax amount</span>
                  <span className="mono">{formatCurrency(tax_amount)}</span>
                </div>
              )}
              <div className="border-t border-[#D4AF37]/30 pt-3 flex justify-between items-baseline">
                <span className="overline text-[#D4AF37]">Grand Total</span>
                <span className="serif text-2xl text-[#D4AF37]" data-testid="seller-summary-grand-total">{formatCurrency(grand_total)}</span>
              </div>
              <div className="border-t border-white/5 pt-2 flex justify-between text-[0.65rem]">
                <span className="text-[#8A8A8A]">Your commission @ {rate.toFixed(1)}%</span>
                <span className="mono text-[#D4AF37]" data-testid="seller-commission-preview">{formatCurrency(commissionPreview)}</span>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="btn-outline-gold px-5 py-2.5">Cancel</button>
            <button
              type="submit"
              data-testid="submit-seller-sale"
              className="btn-gold px-5 py-2.5"
              disabled={submitting || anyBlocked || !allLinesFilled}
            >
              {submitting ? "Saving…" : `Record Invoice · ${formatCurrency(grand_total)}`}
            </button>
          </div>
        </form>
      </div>
      {/* POS scanner modal */}
      {showScanner && (
        <QrScannerModal
          title="Scan QR to add"
          onClose={() => setShowScanner(false)}
          onScan={handleScannedProduct}
        />
      )}
    </div>
  );
}

function MySalesTable({ sales, onDownloadInvoice }) {
  const pieceCell = (s) => {
    if (s.is_multi && s.items_count > 1) {
      return (
        <div className="min-w-0">
          <div className="truncate">{s.items_count} items</div>
          <div className="text-[0.65rem] text-[#666] mono mt-0.5 truncate" title={s.items?.map((it) => it.product_name).join(", ")}>
            {s.items?.slice(0, 2).map((it) => it.product_name).join(", ")}
            {s.items_count > 2 ? ` +${s.items_count - 2}` : ""}
          </div>
        </div>
      );
    }
    return (
      <div className="min-w-0">
        <div className="truncate">{s.product_name}</div>
        <div className="text-[0.65rem] text-[#666] mono mt-0.5">
          {formatQty(s.quantity, s.product_unit, s.product_custom_unit)}
        </div>
      </div>
    );
  };

  const columns = [
    { key: "invoice", label: "Invoice", width: "120px", cell: (s) => <span className="mono text-xs text-[#D4AF37]">{s.invoice_number}</span> },
    { key: "date", label: "Date", width: "110px", cell: (s) => <span className="mono text-xs text-[#8A8A8A]">{s.created_at?.slice(0, 10)}</span>, hideOnMobile: true },
    { key: "piece", label: "Piece", width: "30%", cell: pieceCell },
    { key: "customer", label: "Customer", cell: (s) => <span className="text-[#8A8A8A] truncate">{s.customer_name}</span> },
    { key: "total", label: "Total", align: "right", width: "140px", cell: (s) => <span className="mono">{formatCurrency(s.total)}</span> },
  ];

  const rowActions = (s) => (
    <>
      {whatsappInvoiceLink(s) && (
        <a
          href={whatsappInvoiceLink(s)}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`whatsapp-${s.invoice_number}`}
          className="min-h-[44px] sm:min-h-0 text-[#7BA585] hover:text-[#9DC9A9] text-xs uppercase tracking-wider inline-flex items-center gap-1.5 px-2 py-2"
          title={`Send to ${s.customer_phone}`}
        >
          <MessageCircle className="w-3 h-3" /> WhatsApp
        </a>
      )}
      <button
        onClick={() => onDownloadInvoice(s)}
        data-testid={`invoice-${s.invoice_number}`}
        className="min-h-[44px] sm:min-h-0 text-[#D4AF37] hover:text-[#F3E5AB] text-xs uppercase tracking-wider inline-flex items-center gap-1.5 px-2 py-2"
      >
        <FileText className="w-3 h-3" /> Invoice
      </button>
    </>
  );

  return (
    <ResponsiveTable
      columns={columns}
      rows={sales}
      rowKey={(s) => s.id}
      testid="my-sales-table"
      rowActions={rowActions}
      emptyMessage="No sales yet — record your first sale to start earning commission."
      mobileCard={(s) => ({
        title: <span className="text-[#D4AF37] mono text-xs">{s.invoice_number}</span>,
        subtitle: <span>{s.customer_name} · {s.created_at?.slice(0, 10)}</span>,
        fields: [
          { label: "Total", value: <span className="mono">{formatCurrency(s.total)}</span> },
          { label: "Piece", value: pieceCell(s) },
          { label: "Payment", value: <span>{s.payment_method || "—"}</span> },
        ],
        actions: rowActions(s),
      })}
    />
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="overline mb-2">{label}</div>
      {children}
    </div>
  );
}
